import { useCallback, useContext, useEffect, useState } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import TranscriptionDialogWrapper from '../TranscriptionDialog/TranscriptionDialogWrapper';
import SongRecorder from '../../SongRecorder/SongRecorder';
import { TranscriptionDialogContext, UserContext } from '../../../../context';
import RecordingPlayback from '../../../molecules/AudioRecorder/RecordingPlayback';
import { SheetSourceType } from '../../../../entities';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    width: '100%',
  },
  playbackContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
    width: '100%',
  },
}));

export interface RecordingDialogProps {
  open: boolean;
  handleClose: () => void;
  handleResolve: () => void;
}

const RecordingDialog: React.FC<RecordingDialogProps> = ({ open, handleClose, handleResolve }) => {
  // const tag = `[${RecordingDialog.name}] -`;

  const { classes } = useStyles();
  const { t } = useTranslation();
  const { file, setFile, setSettings, setApriori, setSource } = useContext(
    TranscriptionDialogContext,
  );
  const { user } = useContext(UserContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [title, setTitle] = useState('');
  const [helperText, setHelperText] = useState('');
  const [allowRecording, setAllowRecording] = useState(true);
  const [minDurationReached, setMinDurationReached] = useState(false);

  const [playing, setPlaying] = useState(false);

  const handleBackClick = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setTitle(t('home.input.dialog.recording.title'));
        setHelperText(t('home.input.dialog.recording.help'));
        setPlaying(false);
        break;
      case 1:
        setTitle(t('home.input.dialog.recording_playback.title'));
        setHelperText(t('home.input.dialog.recording_playback.help'));
        break;
      default:
        break;
    }
  }, [currentStep, t]);

  const handleMinDurationReached = () => {
    setMinDurationReached(true);
  };

  const handleRecordingComplete = useCallback(
    (audioBlob: Blob, bpm?: number, timeSig?: number, startTimeOffset?: number) => {
      const recordingFile = new File([audioBlob], 'recording', { type: audioBlob.type });
      setFile(recordingFile);
      setMinDurationReached(false);
      setAllowRecording(true);
      setSource(SheetSourceType.Microphone);
      if (bpm && timeSig) {
        let timeSigStr = null;
        if (timeSig === 4) {
          timeSigStr = '4/4';
        } else if (timeSig === 3) {
          timeSigStr = '3/4';
        }
        let startTime = 0;
        if (startTimeOffset !== undefined) {
          startTime = startTimeOffset / 1000;
        }
        setSettings({
          time_signature: timeSigStr,
          exact_tempo: bpm,
          start_time: startTime,
        });
      }

      setApriori({
        title: `My Song ${new Date().toLocaleString()}`,
        composer: user ? `${user.firstName} ${user.lastName}` : 'unknown',
      });
    },
    [setFile, setMinDurationReached, setAllowRecording, setSource, setSettings, setApriori, user],
  );
  return (
    <TranscriptionDialogWrapper
      open={open}
      handleClose={handleClose}
      currentStep={currentStep}
      title={title}
      numberOfSteps={2}
      handleBackClick={handleBackClick}
      submitting={false}
      helperText={helperText}
    >
      <div className={classes.container}>
        <div style={{ display: currentStep === 0 ? 'block' : 'none', width: '100%' }}>
          <SongRecorder
            onRecordingComplete={handleRecordingComplete}
            minDuration={5}
            maxDuration={300}
            onMinDurationReached={handleMinDurationReached}
            allowRecording={allowRecording && currentStep === 0}
          />
          <div className={classes.buttonContainer}>
            <Button
              id="btn_recording_next"
              onClick={() => setCurrentStep(1)}
              variant="contained"
              disabled={!file && !minDurationReached}
            >
              {t('home.input.dialog.next')}
            </Button>
          </div>
        </div>
        {currentStep === 1 && (
          <>
            <div className={classes.playbackContainer}>
              <IconButton
                id={playing ? 'btn_recording_playback_pause' : 'btn_recording_playback_play'}
                aria-label="play/pause"
                onClick={() => setPlaying(!playing)}
                size="large"
              >
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <RecordingPlayback playing={playing} audioFile={file} />
            </div>
            <ul>
              {Array.from({ length: 2 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>{t(`home.input.dialog.recording_playback.tips.${i}`)}</li>
              ))}
            </ul>
            <div className={classes.buttonContainer}>
              <Button
                id="btn_recording_playback_next"
                onClick={() => handleResolve()}
                variant="contained"
              >
                {t('home.input.dialog.next')}
              </Button>
            </div>
          </>
        )}
      </div>
    </TranscriptionDialogWrapper>
  );
};

export default RecordingDialog;
